import Promise from "promise-polyfill";
import { createError, w } from './utilities';
import { logTelemetryEvent } from './logger';
import { ErrorSeverity } from '@mecontrol/common';

declare global {
    interface Window {
        Promise?: PromiseConstructorLike;
    }
}

/*
 * If native Promise is supported, use that to queue our promise polyfill's deferred fns.
 * This hook implementation improves the performance of our promises by running deferred
 * functions as microtasks instead of whenever the browser fires a timer
 *
 * [Learn more about microtask and browser queues]: https://youtu.be/cCOL7MC4Pl0
 */
if (typeof w.Promise !== "undefined" && w.Promise.toString().indexOf('[native code]') !== -1) {
    Promise._immediateFn = callback => {
        return (w.Promise as any).resolve().then(callback);
    };
}

const originalUnhandledFn = Promise._unhandledRejectionFn;
Promise._unhandledRejectionFn = function (error: any): void {
    if (process.env.NODE_ENV === "development") {
        originalUnhandledFn(error);
    }

    logTelemetryEvent({
        eventType: "ClientError",
        isCritical: true,
        name: error.message || '(falsey message property on error)', // Just to be safe...
        type: "UnhandledPromiseRejection",
        details: error.stack || '', // IE11 Error objects don't have stack property
        displayed: false,
        severity: error.mcIsTimeout ? ErrorSeverity.Warning : ErrorSeverity.Error
    });
};

function timeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(createError(`Promise timed out after ${ms}ms.`, true));
        }, ms);

        promise
            .then(resolve, reject)
            .finally(() => { clearTimeout(timeoutId); });
    });
}

export {
    Promise,
    timeout,
};
